// eslint-disable-next-line
// @ts-nocheck
import '../components/nav/nav';
import Sidebar from '../components/sidebar';
import ContextMenu from '../components/contextMenu';
import alertModule from '../components/alert';
import './NavigationPanelTabs';
import utils from '../core/utils';
import secondaryMenu from './secondaryMenu';
import { getFavoritePanel } from './FavoritePanel';
import { itemType as itemTypeMetadata } from 'metadata';
import { commands } from 'cui';
import { favorites as favoriteMethods } from 'store';
import { wire } from 'hyperhtml';
import * as Inferno from 'inferno';
import { SvgManager, getResource } from 'core';
import {
	contentTabId,
	defaultTabs,
	favoriteTabId
} from './sidebarDataConverter';
import type { Favorite } from 'Modules/store/storeTypes';

const icons = {
	back: '../images/BackFull.svg',
	close: '../images/Close.svg',
	defaultItemType: '../images/DefaultItemType.svg',
	search: '../images/ExecuteSearch.svg',
	open: '../images/OpenInTab.svg',
	pinned: '../images/PinnedOn.svg',
	savedSearch: '../images/SavedSearchOverlay.svg',
	unpinned: '../images/PinnedOff.svg',
	more: '../images/More.svg'
};

SvgManager.enqueue(Object.values(icons));

const disabledRowHighlightingClass =
	'aras-navigation-panel_row-highlighting-disabled';

function navFormatter(nav, item) {
	const itemValue = item.value;
	if (itemValue.children) {
		return null;
	}

	const isDashboardItem = itemValue.dashboard;
	if (itemValue.itemTypeId || isDashboardItem) {
		const leafTemplate = nav.templates.getDefaultTemplate(item);
		const src = isDashboardItem ? icons.open : icons.search;
		const image = Inferno.createVNode(
			Inferno.getFlagsForElementVnode('aras-image'),
			'aras-image',
			'aras-nav-leaf-ico-image',
			null,
			utils.infernoFlags.unknownChildren,
			{
				'aria-hidden': true,
				src
			}
		);
		const nodeClassList = isDashboardItem
			? 'aras-nav-leaf-ico'
			: 'aras-button aras-button_c aras-nav-leaf-ico';
		leafTemplate.push(
			Inferno.createVNode(
				Inferno.getFlagsForElementVnode('span'),
				'span',
				nodeClassList,
				image,
				utils.infernoFlags.unknownChildren
			)
		);

		return leafTemplate;
	}

	return null;
}

function getFavoriteCategories(itemTypeName, options) {
	const favorites = options.favorites;
	const favoritesFilterCriteria = (favoriteItem) => {
		return (
			favoriteItem.quickAccessFlag === '1' &&
			favoriteItem.contextType === itemTypeName
		);
	};
	const [favoriteSearches, favoriteItems] = ['Search', 'Item'].map((type) =>
		favoriteMethods.filterFavorites(favorites, type, favoritesFilterCriteria)
	);

	const favoriteCategories = [];
	if (favoriteSearches.length) {
		const label = getResource(
			'navigation_panel.secondary_menu.favorite_searches'
		);
		favoriteCategories.push({
			icon: options.icon,
			overlayIcons: [icons.savedSearch],
			unpinIcon: icons.close,
			label,
			items: favoriteSearches
		});
	}
	if (favoriteItems.length) {
		favoriteCategories.push({
			icon: options.icon,
			label: options.pluralLabel,
			items: favoriteItems
		});
	}
	return favoriteCategories;
}

async function getItemTypeData(itemTypeId) {
	const itemType = await itemTypeMetadata.getItemType(itemTypeId, 'id');
	const itemTypeName = itemType.name;
	const singularLabel = itemType.label || itemTypeName;
	const propertiesWithKeyedName =
		itemTypeMetadata.getKeyedNameConfiguration(itemType);
	const newCommand = createCommand('OpenNewItem', itemType);
	const canAdd = newCommand.canExecute();
	const openForbidden = window.isFunctionDisabled(itemTypeName, 'Open');
	const isQuickSearchDisabled =
		openForbidden || !propertiesWithKeyedName.length;

	return {
		canAdd,
		isQuickSearchDisabled,
		itemType,
		itemTypeName,
		singularLabel
	};
}

function createCommand(commandName, itemType, options = {}) {
	const itemTypeName = itemType?.name;

	return commands.commandFactory(commandName, {
		itemType,
		itemTypeName,
		location: 'MainWindowView',
		...options
	});
}

export default class NavigationPanel extends Sidebar {
	currentTab = contentTabId;
	staticTabs = defaultTabs.map((tab) => tab.id);
	nav = document.createElement('aras-nav');
	tabs = document.createElement('aras-navigation-panel-tabs');
	topScroll = null;
	tocExpandedBeforeSearch = null;

	state: {
		secondaryMenuItemTypeId: string | null;
		secondaryMenuData: {
			itemTypeId: string;
		} | null;
		favorites: Record<string, Favorite>;
	} = {
		secondaryMenuItemTypeId: null,
		secondaryMenuData: null,
		favorites: {}
	};

	togglePin(pinned) {
		super.togglePin(pinned);
		this.render();
	}

	toggleVisibility(visible) {
		super.toggleVisibility(visible);
		const splitter = this.nextElementSibling;
		const isVisible = this.isVisible;
		splitter.classList.toggle('aras-hide', !isVisible);
		if (isVisible && this.isPinned) {
			setTimeout(() => {
				this.focus();
			});
		}
	}

	focus() {
		if (!this.state.secondaryMenuItemTypeId) {
			return super.focus();
		}

		const quickSearch = this.querySelector(
			'.aras-secondary-menu__quick-search:not([disabled])'
		);

		if (quickSearch) {
			quickSearch.focus();
		} else {
			const navHeader = this.querySelector('.aras-navigation-panel__header');
			navHeader.focus();
		}
	}

	#createHeaderIcon(src) {
		return wire(this, ':headerIcon')`<aras-image
			aria-hidden="true"
			src="${src}"
			class="aras-navigation-panel__header-icon"
		/>`;
	}

	#createBackButton() {
		const backTitle = getResource(
			'navigation_panel.secondary_menu.back_to_contents'
		);

		return wire(this, ':backButton')`<button
			class="${'aras-button aras-button_c'}"
			onclick="${() => this._hideSecondaryMenu()}"
			title="${backTitle}"
			>
			<aras-image
				aria-hidden="true"
				class="aras-button__icon"
				src="${icons.back}"
			/>
		</button>`;
	}

	#createHeader({
		title,
		iconSrc
	}: {
		title: string;
		iconSrc: string;
	}): HTMLElement {
		const isSecondaryMenu = this.currentTab === 'secondary-tab';
		const pinIcon = this.isPinned ? icons.pinned : icons.unpinned;

		const backButton = isSecondaryMenu ? this.#createBackButton() : null;
		const headerIcon = iconSrc ? this.#createHeaderIcon(iconSrc) : null;
		const favoriteMenuIcon =
			this.currentTab === favoriteTabId
				? wire()`
					<span class="aras-navigation-panel__favorite-menu-icon aras-button aras-button_c">
						<aras-image aria-hidden="true" class="aras-button__icon" src="${icons.more}" />
					</span>`
				: null;

		return wire(this, ':header')`
			<div class="aras-navigation-panel__header" tabindex="0" aria-label="${title}">
				${backButton}
				${headerIcon}
				<span class="aras-navigation-panel__header-title">
					${title}
				</span>
				${favoriteMenuIcon}
				<span
					class="aras-navigation-panel__pin-icon aras-button aras-button_c"
					onclick="${() => this.togglePin()}"
				>
					<aras-image aria-hidden="true" class="aras-button__icon" src="${pinIcon}" />
				</span>
			</div>`;
	}
	
	#createSearchBar(): HTMLElement {
		return wire(this, ':searchbar')`
			<div class="aras-navigation-panel__search">
				<div class="aras-navigation-panel__search-wrapper">
					<input
						id="filterTOC"
						class="aras-navigation-panel__search-input"
						type="text"
						placeholder="Search Contents..."
						oninput="${(e) => this._filterTOC(e)}"
					/>

					<button
						type="button"
						class="aras-navigation-panel__search-clear"
						title="Clear search"
						aria-label="Clear search"
						hidden
						onclick="${() => this._clearTOCFilter()}"
					>
						<aras-image
							aria-hidden="true"
							src="${icons.close}"
						/>
					</button>
				</div>
			</div>
		`;
	}
	
	_clearTOCFilter(): void {
		const input = this.querySelector( '.aras-navigation-panel__search-input' ) as HTMLInputElement;
		const clearButton = this.querySelector( '.aras-navigation-panel__search-clear' ) as HTMLElement;
		if (input) {
			input.value = '';
			input.focus();
		}

		if (clearButton) {
			clearButton.hidden = true;
		}
		this._restoreTOC();
	}
	
	_filterTOC(e: Event): void {
		const input = e.target as HTMLInputElement;
		const clearButton = this.querySelector( '.aras-navigation-panel__search-clear' ) as HTMLElement;

		if (clearButton) {
			clearButton.hidden = input.value.length === 0;
		}
		const searchValue = input.value.trim().toLowerCase();

		const rootList = this.nav.querySelector('ul.aras-nav');

		if (!rootList) {
			console.warn('TOC root list was not found.');
			return;
		}

		if (searchValue === '') {
			this._restoreTOC();
			return;
		}

		if (this.tocExpandedBeforeSearch === null) {
			this.tocExpandedBeforeSearch = new Set();

			this.nav
				.querySelectorAll(
					'.aras-nav__parent.aras-nav__parent_expanded'
				)
				.forEach((category: HTMLElement) => {
					const key = category.dataset.key;

					if (key) {
						this.tocExpandedBeforeSearch.add(key);
					}
				});
		}

		Array.from(rootList.children).forEach(
			(node: HTMLElement) => {
				if (
					node.classList.contains('aras-nav__parent') ||
					node.classList.contains('aras-nav__child')
				) {
					this._filterTOCNode(node, searchValue);
				}
			}
		);
	}

	_filterTOCNode(
	node: HTMLElement,
	searchValue: string
): boolean {
	const isParent =
		node.classList.contains('aras-nav__parent');

	// -----------------------------------------
	// CATEGORY / SUBCATEGORY
	// Do NOT search its own label.
	// Only show it if a descendant ItemType matches.
	// -----------------------------------------
	if (isParent) {
		const childContainer = node.querySelector(
			':scope > .aras-nav__child-container'
		);

		let hasMatchingChild = false;

		if (childContainer) {
			Array.from(childContainer.children).forEach(
				(child: HTMLElement) => {
					if (
						!child.classList.contains('aras-nav__parent') &&
						!child.classList.contains('aras-nav__child')
					) {
						return;
					}

					if (this._filterTOCNode(child, searchValue)) {
						hasMatchingChild = true;
					}
				}
			);
		}

		node.style.display =
			hasMatchingChild ? '' : 'none';

		node.classList.toggle(
			'aras-nav__parent_expanded',
			hasMatchingChild
		);

		node.setAttribute(
			'aria-expanded',
			String(hasMatchingChild)
		);

		return hasMatchingChild;
	}

	// -----------------------------------------
	// ITEMTYPE / LEAF
	// This is what we actually search.
	// -----------------------------------------
	const label = node.querySelector(
		':scope > .aras-nav__label'
	);

	const labelText =
		label?.textContent?.trim().toLowerCase() || '';

	const matches =
		labelText.includes(searchValue);

	node.style.display =
		matches ? '' : 'none';

	return matches;
}
	
	_restoreTOC(): void {
		this.nav
			.querySelectorAll(
				'.aras-nav__parent, .aras-nav__child'
			)
			.forEach((node: HTMLElement) => {
				node.style.display = '';
			});

		this.nav
			.querySelectorAll('.aras-nav__parent')
			.forEach((category: HTMLElement) => {
				const key = category.dataset.key;

				const wasExpanded =
					Boolean(
						key &&
						this.tocExpandedBeforeSearch &&
						this.tocExpandedBeforeSearch.has(key)
					);

				category.classList.toggle(
					'aras-nav__parent_expanded',
					wasExpanded
				);

				category.setAttribute(
					'aria-expanded',
					String(wasExpanded)
				);
			});

		this.tocExpandedBeforeSearch = null;
	}
	
	#getCurrentTab(): { title: string; component: HTMLElement } {
		switch (this.currentTab) {
			case contentTabId:
				return {
					title: getResource('common.contents'),
					component: this.nav
				};
			case favoriteTabId:
				return {
					title: getResource('common.favorites'),
					component: getFavoritePanel(this)
				};
			default:
				return {
					title: this.state.secondaryMenuData?.pluralLabel,
					component: secondaryMenu(this)
				};
		}
	}
	


	render() 
	{
		this.state.secondaryMenuData = this._getSecondaryMenuData();
		const secondaryMenuData = this.state.secondaryMenuData;

		const { title, component } = this.#getCurrentTab();
		this.html`
			${this.#createHeader({
				title,
				iconSrc: secondaryMenuData?.icon
			})}

			${this.currentTab === contentTabId
				? this.#createSearchBar()
				: null}

			${this.tabs}
			${component}
		`;
	this.nav.scrollTop = this.topScroll;
}

	connectedCallback() {
		super.connectedCallback();
		this.classList.add('aras-navigation-panel', disabledRowHighlightingClass);
		this.setAttribute('role', 'navigation');
		this.popupMenu = new ContextMenu();
		this.secondaryPopupMenu = new ContextMenu();
		this._initContextMenuHandlers();
		this._initContextMenuListeners();
		const observer = new MutationObserver((mutations) => {
			const navPanel = mutations[0].target;
			const panelWidth = navPanel.style.width;
			navPanel.nextElementSibling.style.left = panelWidth;
		});

		observer.observe(this, {
			attributes: true,
			attributeFilter: ['style']
		});
		this.render();
	}

	created() {
		this._initTOC();
		this._initTabs();
	}

	resetState() {
		this.state.secondaryMenuData = null;
		this.render();
	}

	_initContextMenuHandlers() {
		const sidebarTabs = this.tabs;
		const contextMenuMethods = {
			pinItemType: (id, tabData) => sidebarTabs.addTab(id, tabData),
			unpinItemType: (id) => {
				const tabsData = sidebarTabs.data;
				const removedTab = tabsData.get(id);
				sidebarTabs.removeTab(id);
				return removedTab;
			},
			getItemTypeByItemTypeId: (itemTypeId) => {
				let tabId;
				const tabsData = sidebarTabs.data;
				tabsData.forEach((data, id) => {
					if (data.itemTypeId === itemTypeId) {
						tabId = id;
					}
				});
				return tabsData.get(tabId);
			},
			openSearch: (...args) => window.arasTabs.openSearch(...args)
		};
		const dispatchContextMenuEvent = (controlName, event, options) => {
			event.preventDefault();
			const detail = {
				controlName,
				x: event.clientX,
				y: event.clientY,
				...options
			};
			const cuiEvent = new CustomEvent('navigationPanelContextMenu', {
				detail
			});
			this.dispatchEvent(cuiEvent);
		};
		sidebarTabs.on('contextmenu', (itemKey, event) => {
			const currentTarget = sidebarTabs.data.get(itemKey);
			const itemTypeName = currentTarget.contextType;
			const options = {
				currentTarget,
				itemTypeName,
				...contextMenuMethods
			};
			dispatchContextMenuEvent('MainView.PopupMenu', event, options);
		});
		const nav = this.nav;
		nav.on('contextmenu', (itemKey, event) => {
			nav.select(itemKey);
			const currentTarget = nav.data.get(itemKey);
			const itemTypeId = currentTarget.itemTypeId;
			const options = {
				currentTarget,
				itemTypeId,
				...contextMenuMethods
			};
			dispatchContextMenuEvent('MainView.PopupMenu', event, options);
		});
		this._favoritesContextMenuHandler = (event) => {
			const favoriteItemRow = event.target.closest('.aras-nav__child');
			if (!favoriteItemRow) {
				return;
			}

			favoriteItemRow.classList.add('aras-nav__child_selected');
			const unselectHighlightedItem = () => {
				favoriteItemRow.classList.remove('aras-nav__child_selected');
				this.popupMenu.dom.removeEventListener(
					'contextMenuClose',
					unselectHighlightedItem
				);
			};
			this.popupMenu.dom.addEventListener(
				'contextMenuClose',
				unselectHighlightedItem
			);

			const favoriteId = favoriteItemRow.dataset.key;
			const currentTarget = this.state.favorites[favoriteId];
			const itemTypeName = currentTarget.contextType;
			const options = {
				currentTarget,
				itemTypeName
			};
			dispatchContextMenuEvent(
				'MainView.PopupMenuSecondaryMenu',
				event,
				options
			);
		};
	}

	_initContextMenuListeners() {
		const contextMenuDom = this.popupMenu.dom;
		contextMenuDom.addEventListener('contextMenuShow', () => {
			this.classList.remove(disabledRowHighlightingClass);
		});
		contextMenuDom.addEventListener('contextMenuClose', (e) => {
			this.classList.add(disabledRowHighlightingClass);
			if (e.detail.type === 'keydown') {
				this.focus();
				return;
			}

			if (!this.isPinned) {
				setTimeout(() => {
					this.closeIfNotAcitive(document.activeElement);
				});
			}
		});
		contextMenuDom.addEventListener('click', () => {
			this._hideIfNotPinned();
		});
	}

	closeIfNotAcitive(element) {
		const isElement = Boolean(element && this.contains(element));
		const isContextMenu = Boolean(
			element &&
				(this.popupMenu.dom.contains(element) ||
					this.secondaryPopupMenu.dom.contains(element))
		);
		if (!(isElement || isContextMenu)) {
			this.toggleVisibility(false);
		}
	}

	_initTOC() {
		const selectTab = (dataItem) => {
			const tabs = this.tabs;
			tabs.data.forEach((tabItem, id) => {
				if (tabItem.itemTypeId === dataItem.itemTypeId) {
					tabs.selectTab(id);
				}
			});
		};
		this.nav.classList.add('aras-nav-toc');
		this.nav.formatter = navFormatter.bind(null, this.nav);
		const handler = async (itemKey, event) => {
			if (
				event.type === 'keydown' &&
				!['Enter', 'Space', 'NumpadEnter'].includes(event.code)
			) {
				return;
			}
			const dataItem = this.nav.data.get(itemKey);
			const isNewTabOpened =
				dataItem.dashboard ||
				event.target.closest('.aras-button.aras-nav-leaf-ico');
			if (isNewTabOpened) {
				this._hideIfNotPinned();
			} else if (dataItem.itemTypeId) {
				this._showSecondaryMenu(dataItem.itemTypeId);
				selectTab(dataItem);
			}
		};
		this.nav.on('click', handler);
		this.nav.on('keydown', handler);
	}

	_initTabs() {
		this.tabs.on('select', (id) => {
			if (this.staticTabs.includes(id)) {
				this._hideSecondaryMenu(id);
				return;
			}

			const item = this.tabs.data.get(id);
			this._showSecondaryMenu(item.itemTypeId);
		});

		this.tabs.on('click', (id) => {
			if (this.staticTabs.includes(id)) {
				this._hideSecondaryMenu(id);
			}
		});
	}

	_hideIfNotPinned() {
		if (!this.isPinned) {
			this.toggleVisibility(false);
			document.documentElement.focus(); // for correct work of sidebar in IE and FF
		}
	}

	_getSecondaryMenuData() {
		const itemTypeId = this.state.secondaryMenuItemTypeId;
		if (!itemTypeId) {
			return null;
		}

		const items = [...this.nav.data.values()];
		const item = items.find(
			(item) => item.itemTypeId === itemTypeId && !item.dashboard
		);
		if (!item) {
			this._hideSecondaryMenu();
			return null;
		}

		const { icon = icons.defaultItemType, label: pluralLabel } = item;
		const itemTypeName = itemTypeMetadata.getItemTypeName(itemTypeId);
		const favoriteCategories = getFavoriteCategories(itemTypeName, {
			icon,
			pluralLabel,
			favorites: this.state.favorites
		});

		if (this.state.secondaryMenuData?.itemTypeId === itemTypeId) {
			return {
				...this.state.secondaryMenuData,
				favoriteCategories
			};
		}

		const loggedUserIdentity = aras.getIsAliasIdentityIDForLoggedUser();
		const secondaryMenuData = {
			canAdd: false,
			favoriteCategories,
			icon,
			itemTypeId,
			itemTypeName,
			isQuickSearchDisabled: true,
			loggedUserIdentity,
			pluralLabel,
			singularLabel: itemTypeName
		};

		this._updateSecondaryMenuData(itemTypeId);

		return secondaryMenuData;
	}

	_showSecondaryMenu(itemTypeId) {
		this.currentTab = 'secondary-tab';
		this.state.secondaryMenuItemTypeId = itemTypeId;
		this.topScroll = this.nav.scrollTop;

		super.focus();
		this.render();
		this.focus();
	}

	_hideSecondaryMenu(tabId = contentTabId) {
		this.currentTab = tabId;
		this.state.secondaryMenuItemTypeId = null;
		this.tabs.selectTab(tabId);

		super.focus();
		this.render();
		this.nav.focus();
	}

	async _updateSecondaryMenuData(itemTypeId) {
		const itemTypeData = await getItemTypeData(itemTypeId);
		if (this.state.secondaryMenuItemTypeId !== itemTypeId) {
			return;
		}

		this.state.secondaryMenuData = {
			...this.state.secondaryMenuData,
			...itemTypeData
		};
		this.render();
		this.focus();
	}

	async _favoritesClick(event) {
		const favoriteItemRow = event.target.closest('.aras-nav__child');
		if (!favoriteItemRow) {
			return;
		}

		const favoriteId = favoriteItemRow.dataset.key;
		const favoriteItem = this.state.favorites[favoriteId];
		const unpinIconNode = event.target.closest(
			'.aras-button.aras-nav-leaf-ico'
		);
		if (unpinIconNode) {
			favoriteMethods.removeFromQuickAccess(favoriteId);
		} else if (favoriteItem.category === 'Item') {
			const favoriteItemType = favoriteItem.contextType;
			const itemType = await itemTypeMetadata.getItemType(favoriteItemType);
			const isVersionable = itemType['is_versionable'] === '1';
			const getItemMethod = isVersionable
				? 'getItemLastVersion'
				: 'getItemById';
			const itemNode = aras[getItemMethod](
				favoriteItemType,
				favoriteItem.additional_data.id,
				isVersionable ? false : 0
			);
			const openCommand = createCommand('OpenItem', itemType, { itemNode });
			if (openCommand.canExecute()) {
				await openCommand.execute();

				return this._hideIfNotPinned();
			}

			const errorMessage = getResource(
				'ui_methods.access_restricted_or_not_exist',
				favoriteItemType
			);
			alertModule(errorMessage, {
				type: 'error'
			});
		} else if (favoriteItem.category === 'Search') {
			let itemTypeId = this.state.secondaryMenuItemTypeId;
			if (!itemTypeId) {
				const itemTypeName = favoriteItem.contextType;
				itemTypeId = itemTypeMetadata.getItemTypeId(itemTypeName);
			}
			this._searchItemType(itemTypeId, favoriteId);
		}
	}

	async _createNewItem() {
		const { itemType } = this.state.secondaryMenuData;
		const openNewItemCommand = createCommand('OpenNewItem', itemType);

		await openNewItemCommand.execute();

		this._hideIfNotPinned();
	}

	_searchItemType(itemTypeId, favoriteSearchId) {
		return Promise.resolve(
			window.arasTabs.openSearch(itemTypeId, favoriteSearchId)
		).then(() => {
			this._hideIfNotPinned();
		});
	}

	async _openItem(event) {
		const quickSearch = event.currentTarget;
		const selectedItem = quickSearch.getSelectedItem();
		if (!selectedItem) {
			return;
		}
		quickSearch.clear();

		const { itemType } = this.state.secondaryMenuData;
		const itemNode = aras.getItemById(itemType.name, selectedItem.itemId);
		const openCommand = createCommand('OpenItem', itemType, { itemNode });
		if (openCommand.canExecute()) {
			openCommand.execute();
		}
	}
}

NavigationPanel.define('aras-navigation-panel');
